import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DatePickerProps } from '@components/date-picker'
import { useScrollListener } from '@hooks/scroll-position'

import { useScrollAnchor } from '@hooks/scroll-anchor'
import { VenueMap } from '@modules/venue/views/venue-map'
import { InventoryItem, Venue, VenueDaySettings, VenueEvent } from 'types/model'
import {
  VenueLayout,
  VenueLayoutSection,
  VenueLayoutTable,
} from 'types/model/venue-layout'
import { InventoryListHeader } from './inventory-list-header'
import { InventoryListing } from './inventory-listing'
import { ReservationRequestButton } from '@modules/booking/views/reservation-request-button'
import { GuestlistRequestButton } from '@modules/booking/views/guestlist-request-button'
import { ExternalTicketButton } from './external-ticket-button'
import { resolveGuestlistEnabledExternally } from '@modules/venue/daySettings'

export interface InventoryListViewProps {
  venue: Venue
  event?: VenueEvent
  daySettings?: VenueDaySettings
  onInventoryAdd?: (inv: InventoryItem) => void | Promise<void>
  tickets?: InventoryItem[]
  ticketsLoading?: boolean
  packages?: InventoryItem[]
  packagesLoading?: boolean
  date?: string
  minDate?: number
  maxDate?: number
  onDateChange?: DatePickerProps['onChange']
  onReservationModalOpen?: () => void
  onGuestlistModalOpen?: () => void
  showHeader?: boolean
  stickyHeader?: boolean
  showEventHeader?: boolean
  showEventPoster?: boolean
  stickyMap?: boolean
  showMap?: boolean
  layouts?: VenueLayout[]
  sections?: VenueLayoutSection[]
  tables?: VenueLayoutTable[]
  attributionId?: string | null
  affiliateId?: string | null
}

function isLayoutConsumerVisible(layout: VenueLayout): boolean {
  return (layout.visibility ?? 10) >= 10
}

export function InventoryListView({
  venue,
  event,
  tickets,
  ticketsLoading = false,
  packages,
  packagesLoading = false,
  date,
  daySettings,
  minDate,
  maxDate,
  onDateChange,
  onReservationModalOpen,
  onGuestlistModalOpen,
  showHeader = true,
  stickyHeader = true,
  showEventHeader = false,
  showEventPoster = true,
  stickyMap = false,
  showMap = false,
  layouts,
  sections,
  tables,
  onInventoryAdd,
  attributionId,
  affiliateId,
}: InventoryListViewProps) {
  const { t } = useTranslation()

  /** Defer listings + empty copy until after mount so SSR matches first client paint (SWR inventory can disagree). */
  const [inventoryBlockReady, setInventoryBlockReady] = useState(false)
  useEffect(() => {
    setInventoryBlockReady(true)
  }, [])

  const [selectedSection, setSelectedSection] = useState<VenueLayoutSection>()
  const listScrollPosition = useScrollListener()
  const headerRef = useRef<HTMLDivElement>(null)
  const { setScrollAnchor, scrollToAnchor } = useScrollAnchor()

  const noTickets = !tickets?.length && !ticketsLoading
  const noPackages = !packages?.length && !packagesLoading
  const externalSellingLink = event?.externalSellingLink ?? undefined
  const noInventory = !externalSellingLink && noTickets && noPackages
  const loading = ticketsLoading || packagesLoading
  const fewInventoryItems = (tickets?.length ?? 0) + (packages?.length ?? 0) < 4
  const hasLayouts = useMemo(
    () => !!layouts?.filter((layout) => isLayoutConsumerVisible(layout))?.length,
    [layouts]
  )

  const mapView = showMap && hasLayouts

  const {
    defaultReservationRequestEnabled,
  } = venue?.config ?? {}

  const allowReservation =
    daySettings?.reservationRequestEnabled !== undefined
      ? daySettings.reservationRequestEnabled
      : defaultReservationRequestEnabled
  const allowGuestlist = resolveGuestlistEnabledExternally(daySettings, venue)

  const displayTickets = useMemo(() => {
    if (!selectedSection) return tickets
    return tickets?.filter(
      (ticket) => ticket.venueLayoutSection === selectedSection.id
    )
  }, [selectedSection, tickets])

  const displayPackages = useMemo(() => {
    if (!selectedSection) return packages
    return packages?.filter(
      (pack) => pack.venueLayoutSection === selectedSection.id
    )
  }, [packages, selectedSection])

  const mapInventory = useMemo(
    () => [...(packages ?? []), ...(tickets ?? [])],
    [packages, tickets]
  )

  const handleDateChange = useCallback(
    (val?: string | number | null | undefined) => {
      onDateChange?.(val)
      // scrollToAnchor()
    },
    [onDateChange, scrollToAnchor]
  )

  const handleSectionSelect = useCallback(
    (val?: VenueLayoutSection) => {
      setSelectedSection(val)

      // if (stickyMap) {
      //   scrollToAnchor()
      // }
    },
    [scrollToAnchor, stickyMap]
  )

  return (
    <div ref={setScrollAnchor}>
      <div
        className={classNames('w-full h-full mx-auto max-w-[672px]', {
          '@4xl:flex @4xl:items-start @4xl:space-y-0 @4xl:!max-w-none': mapView,
        })}
        ref={listScrollPosition.listener}
      >
        <div
          className={classNames(
            'w-full h-full max-w-[672px] mx-auto space-y-3 sm:pt-0 z-[1] flex-[5]',
            {
              'bg-inherit sticky top-0': stickyHeader && !fewInventoryItems,
              relative: fewInventoryItems || !stickyHeader,
              '@4xl:flex @4xl:flex-col @4xl:sticky @4xl:top-0': mapView,
            }
          )}
        >
          {showHeader && (
            <div
              className={classNames({
                'background-basic': stickyHeader && !fewInventoryItems,
              })}
            >
              <InventoryListHeader
                date={date}
                onDateChange={onDateChange ? handleDateChange : undefined}
                venue={venue}
                event={showEventHeader ? event : undefined}
                showEventPoster={showEventPoster}
                shadow={!listScrollPosition.atTop}
                ref={headerRef}
                minDate={minDate}
                maxDate={maxDate}
              />
            </div>
          )}
          {mapView && venue && (
            <div className='px-3 hidden @4xl:!block'>
              <VenueMap
                selectedSection={selectedSection}
                onSectionSelect={handleSectionSelect}
                venue={venue}
                layouts={layouts}
                sections={sections}
                tables={tables}
                inventory={mapInventory}
              />
            </div>
          )}
        </div>
        <div
          className={classNames('w-full space-y-6 pt-4 flex-[4]', {
            '@4xl:pt-4': venue && stickyMap,
          })}
        >
          {!loading && externalSellingLink && event?.isSellingActive && (
            <ExternalTicketButton href={externalSellingLink} />
          )}
          {mapView && venue && (
            <div className='px-3 @4xl:hidden'>
              <VenueMap
                selectedSection={selectedSection}
                onSectionSelect={handleSectionSelect}
                venue={venue}
                layouts={layouts}
                sections={sections}
                tables={tables}
                inventory={mapInventory}
              />
            </div>
          )}
          {inventoryBlockReady ? (
            <>
              <div className={classNames({ '@4xl:!mt-0': mapView })}>
                <InventoryListing
                  title={t('buyer.inventory.tickets')}
                  venue={venue}
                  inventory={displayTickets}
                  loading={ticketsLoading}
                  onInventoryAdd={onInventoryAdd}
                >
                  {allowGuestlist ? (
                    <GuestlistRequestButton
                      venue={venue}
                      title={t('buyer.guestlist.join')}
                      date={date}
                      attributionId={attributionId}
                      affiliateId={affiliateId}
                      onGuestlistModalOpen={onGuestlistModalOpen}
                    />
                  ) : null}
                </InventoryListing>
              </div>
              <InventoryListing
                title={t('buyer.inventory.tables_and_packages')}
                venue={venue}
                inventory={displayPackages}
                onInventoryAdd={onInventoryAdd}
                loading={packagesLoading}
              />
            </>
          ) : null}
          {allowReservation ? (
            <div className='flex items-center justify-center w-full px-3'>
              <ReservationRequestButton
                venue={venue}
                date={date}
                attributionId={attributionId}
                affiliateId={affiliateId}
                onReservationModalOpen={onReservationModalOpen}
                title={t('buyer.reservation.request_table')}
              />
            </div>
          ) : null}
          {inventoryBlockReady && noInventory && !loading && (
            <p className='flex items-center justify-center w-full my-6 font-light text-center font-italic'>
              {t('buyer.inventory.no_inventory_for_date')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
